import {
  InventoryContractDefinition,
  InventorySectionDefinition,
  InventorySectionKey,
  InventoryViewDefinition,
  InventoryViewKey,
} from '@/modules/inventory/types';

export const INVENTORY_STATUSES = [
  { value: 'all', label: 'Tất cả trạng thái' },
  { value: 'draft', label: 'Nháp' },
  { value: 'submitted', label: 'Đã gửi duyệt' },
  { value: 'approved', label: 'Đã duyệt' },
  { value: 'receiving', label: 'Đang nhập' },
  { value: 'closed', label: 'Đã đóng' },
  { value: 'posted', label: 'Đã ghi sổ' },
  { value: 'cancelled', label: 'Đã hủy' },
];

export const INVENTORY_SECTIONS: InventorySectionDefinition[] = [
  {
    key: 'dashboard',
    title: 'Inventory Dashboard',
    navLabel: 'Tổng quan',
    description: 'Tổng quan KPI, trạng thái luồng kho và contract frontend/backend của module inventory.',
    path: '/inventory',
    views: ['overview'],
  },
  {
    key: 'catalogs',
    title: 'Danh mục inventory',
    navLabel: 'Danh mục',
    description: 'Shell CRUD cho mặt hàng, đơn vị tính, quy đổi, nhà sản xuất, supplier theo công ty và cấu trúc kho.',
    path: '/inventory/catalogs',
    views: ['items', 'uoms', 'uomConversions', 'manufacturers', 'companySuppliers', 'stores', 'locations', 'lots'],
  },
  {
    key: 'requests',
    title: 'Đề xuất vật tư',
    navLabel: 'Đề xuất',
    description: 'Màn hình khung cho đề xuất vật tư theo đơn hàng và mua dự trữ.',
    path: '/inventory/requests',
    views: ['materialRequests'],
  },
  {
    key: 'purchasing',
    title: 'Mua hàng',
    navLabel: 'Mua hàng',
    description: 'Shell mua hàng, supplier theo công ty và luồng duyệt phiếu mua.',
    path: '/inventory/purchasing',
    views: ['purchaseOrders'],
  },
  {
    key: 'receipts',
    title: 'Nhập kho',
    navLabel: 'Nhập kho',
    description: 'Khung phiếu nhập, ghi sổ và báo cáo nhập theo nhà sản xuất.',
    path: '/inventory/receipts',
    views: ['goodsReceipts'],
  },
  {
    key: 'allocation',
    title: 'Gán vật tư',
    navLabel: 'Gán vật tư',
    description: 'Shell allocation theo đơn hàng, khả dụng và điều chuyển allocation.',
    path: '/inventory/allocation',
    views: ['allocations'],
  },
  {
    key: 'issues',
    title: 'Xuất kho',
    navLabel: 'Xuất kho',
    description: 'Khung phiếu xuất kho, last price và ảnh hưởng đơn hàng.',
    path: '/inventory/issues',
    views: ['issues'],
  },
  {
    key: 'returns',
    title: 'Hoàn trả',
    navLabel: 'Hoàn trả',
    description: 'Khung hoàn trả vật tư theo dòng xuất gốc và đảo giá trị vật tư.',
    path: '/inventory/returns',
    views: ['returns'],
  },
  {
    key: 'stocktakes',
    title: 'Kiểm kê',
    navLabel: 'Kiểm kê',
    description: 'Màn hình snapshot, đếm kiểm kê và xác nhận chênh lệch.',
    path: '/inventory/stocktakes',
    views: ['stocktakes'],
  },
  {
    key: 'reports',
    title: 'Báo cáo inventory',
    navLabel: 'Báo cáo',
    description: 'Report shell cho nhập theo tháng, tỷ lệ vật tư/doanh số và báo cáo tổng hợp công ty mẹ.',
    path: '/inventory/reports',
    views: ['monthlyReceiptsReport', 'orderMaterialRatioReport'],
  },
];

export const INVENTORY_VIEWS: Record<InventoryViewKey, InventoryViewDefinition> = {
  overview: {
    key: 'overview',
    title: 'Tổng quan module',
    description: 'Tổng hợp nhanh tiến độ Phase 1, route inventory và API contract mức 1.',
    createLabel: 'Tạo cấu hình mới',
    updateLabel: 'Cập nhật cấu hình',
    columns: [],
    formFields: [],
  },
  items: {
    key: 'items',
    title: 'Mặt hàng',
    description: 'Danh mục mặt hàng riêng theo công ty.',
    createLabel: 'Tạo mặt hàng',
    updateLabel: 'Cập nhật mặt hàng',
    columns: [
      { key: 'code', label: 'Mã vật tư' },
      { key: 'name', label: 'Tên vật tư' },
      { key: 'manufacturer', label: 'Nhà sản xuất' },
      { key: 'status', label: 'Trạng thái' },
      { key: 'note', label: 'Ghi chú' },
    ],
    formFields: [
      { key: 'code', label: 'Mã vật tư', type: 'text', required: true },
      { key: 'name', label: 'Tên vật tư', type: 'text', required: true },
      { key: 'manufacturer', label: 'Nhà sản xuất', type: 'text' },
      { key: 'status', label: 'Trạng thái', type: 'select', options: INVENTORY_STATUSES.slice(1, 5) },
      { key: 'note', label: 'Ghi chú', type: 'textarea' },
    ],
  },
  uoms: {
    key: 'uoms',
    title: 'Đơn vị tính',
    description: 'Danh mục đơn vị tính dùng riêng theo công ty.',
    createLabel: 'Tạo đơn vị tính',
    updateLabel: 'Cập nhật đơn vị tính',
    columns: [
      { key: 'code', label: 'Mã UOM' },
      { key: 'name', label: 'Tên đơn vị tính' },
      { key: 'status', label: 'Trạng thái' },
      { key: 'note', label: 'Quy tắc làm tròn' },
    ],
    formFields: [
      { key: 'code', label: 'Mã UOM', type: 'text', required: true },
      { key: 'name', label: 'Tên đơn vị tính', type: 'text', required: true },
      {
        key: 'status',
        label: 'Trạng thái',
        type: 'select',
        options: [
          { value: 'draft', label: 'Nháp' },
          { value: 'approved', label: 'Đang dùng' },
        ],
      },
      { key: 'note', label: 'Ghi chú', type: 'textarea' },
    ],
  },
  uomConversions: {
    key: 'uomConversions',
    title: 'Quy đổi đơn vị tính',
    description: 'Tỷ lệ quy đổi và quy tắc làm tròn theo mặt hàng.',
    createLabel: 'Tạo quy đổi',
    updateLabel: 'Cập nhật quy đổi',
    columns: [
      { key: 'code', label: 'Mã quy đổi' },
      { key: 'name', label: 'Mô tả quy đổi' },
      { key: 'qty', label: 'Tỷ lệ', align: 'right' },
      { key: 'status', label: 'Trạng thái' },
    ],
    formFields: [
      { key: 'code', label: 'Mã quy đổi', type: 'text', required: true },
      { key: 'name', label: 'Mô tả quy đổi', type: 'text', required: true },
      { key: 'qty', label: 'Tỷ lệ quy đổi', type: 'number', required: true },
      { key: 'status', label: 'Trạng thái', type: 'select', options: INVENTORY_STATUSES.slice(1, 5) },
    ],
  },
  manufacturers: {
    key: 'manufacturers',
    title: 'Nhà sản xuất',
    description: 'Danh mục nhà sản xuất để lên báo cáo nhập theo tháng.',
    createLabel: 'Tạo nhà sản xuất',
    updateLabel: 'Cập nhật nhà sản xuất',
    columns: [
      { key: 'code', label: 'Mã NSX' },
      { key: 'name', label: 'Tên nhà sản xuất' },
      { key: 'status', label: 'Trạng thái' },
      { key: 'note', label: 'Ghi chú' },
    ],
    formFields: [
      { key: 'code', label: 'Mã nhà sản xuất', type: 'text', required: true },
      { key: 'name', label: 'Tên nhà sản xuất', type: 'text', required: true },
      { key: 'status', label: 'Trạng thái', type: 'select', options: INVENTORY_STATUSES.slice(1, 5) },
      { key: 'note', label: 'Ghi chú', type: 'textarea' },
    ],
  },
  companySuppliers: {
    key: 'companySuppliers',
    title: 'Nhà cung cấp theo công ty',
    description: 'Lớp mapping supplier gốc vào từng công ty inventory.',
    createLabel: 'Tạo supplier công ty',
    updateLabel: 'Cập nhật supplier công ty',
    columns: [
      { key: 'code', label: 'Mã nội bộ' },
      { key: 'name', label: 'Tên nhà cung cấp' },
      { key: 'supplier', label: 'Supplier gốc' },
      { key: 'status', label: 'Trạng thái' },
    ],
    formFields: [
      { key: 'code', label: 'Mã nội bộ công ty', type: 'text', required: true },
      { key: 'name', label: 'Tên hiển thị', type: 'text', required: true },
      { key: 'supplier', label: 'Supplier gốc', type: 'text', required: true },
      { key: 'status', label: 'Trạng thái', type: 'select', options: INVENTORY_STATUSES.slice(1, 5) },
    ],
  },
  stores: {
    key: 'stores',
    title: 'Kho',
    description: 'Kho vật lý theo từng công ty.',
    createLabel: 'Tạo kho',
    updateLabel: 'Cập nhật kho',
    columns: [
      { key: 'code', label: 'Mã kho' },
      { key: 'name', label: 'Tên kho' },
      { key: 'storeName', label: 'Nhóm kho' },
      { key: 'status', label: 'Trạng thái' },
    ],
    formFields: [
      { key: 'code', label: 'Mã kho', type: 'text', required: true },
      { key: 'name', label: 'Tên kho', type: 'text', required: true },
      { key: 'storeName', label: 'Loại kho', type: 'text' },
      { key: 'status', label: 'Trạng thái', type: 'select', options: INVENTORY_STATUSES.slice(1, 5) },
    ],
  },
  locations: {
    key: 'locations',
    title: 'Vị trí kho',
    description: 'Vị trí đặt hàng hóa trong từng kho.',
    createLabel: 'Tạo vị trí',
    updateLabel: 'Cập nhật vị trí',
    columns: [
      { key: 'code', label: 'Mã vị trí' },
      { key: 'name', label: 'Tên vị trí' },
      { key: 'storeName', label: 'Kho' },
      { key: 'status', label: 'Trạng thái' },
    ],
    formFields: [
      { key: 'code', label: 'Mã vị trí', type: 'text', required: true },
      { key: 'name', label: 'Tên vị trí', type: 'text', required: true },
      { key: 'storeName', label: 'Kho', type: 'text', required: true },
      { key: 'status', label: 'Trạng thái', type: 'select', options: INVENTORY_STATUSES.slice(1, 5) },
    ],
  },
  lots: {
    key: 'lots',
    title: 'Lô hàng',
    description: 'Mã lot để truy vết nhập, xuất, kiểm kê.',
    createLabel: 'Tạo lô hàng',
    updateLabel: 'Cập nhật lô hàng',
    columns: [
      { key: 'code', label: 'Mã lot' },
      { key: 'name', label: 'Diễn giải lot' },
      { key: 'date', label: 'Ngày hiệu lực' },
      { key: 'status', label: 'Trạng thái' },
    ],
    formFields: [
      { key: 'code', label: 'Mã lot', type: 'text', required: true },
      { key: 'name', label: 'Diễn giải lot', type: 'text', required: true },
      { key: 'date', label: 'Ngày hiệu lực', type: 'date' },
      { key: 'status', label: 'Trạng thái', type: 'select', options: INVENTORY_STATUSES.slice(1, 5) },
    ],
  },
  materialRequests: {
    key: 'materialRequests',
    title: 'Đề xuất vật tư',
    description: 'Phiếu yêu cầu vật tư theo đơn hàng hoặc mua dự trữ.',
    createLabel: 'Tạo đề xuất vật tư',
    updateLabel: 'Cập nhật đề xuất',
    columns: [
      { key: 'code', label: 'Số đề xuất' },
      { key: 'orderNo', label: 'Đơn hàng' },
      { key: 'storeName', label: 'Kho' },
      { key: 'status', label: 'Trạng thái' },
      { key: 'qty', label: 'SL yêu cầu', align: 'right' },
    ],
    formFields: [
      { key: 'code', label: 'Số đề xuất', type: 'text', required: true },
      { key: 'orderNo', label: 'Đơn hàng', type: 'text' },
      { key: 'storeName', label: 'Kho nhận', type: 'text', required: true },
      { key: 'qty', label: 'Số lượng', type: 'number', required: true },
      { key: 'note', label: 'Diễn giải', type: 'textarea' },
    ],
  },
  purchaseOrders: {
    key: 'purchaseOrders',
    title: 'Phiếu mua',
    description: 'Luồng tạo, duyệt và theo dõi trạng thái nhận hàng.',
    createLabel: 'Tạo phiếu mua',
    updateLabel: 'Cập nhật phiếu mua',
    columns: [
      { key: 'code', label: 'Số phiếu mua' },
      { key: 'supplier', label: 'Nhà cung cấp' },
      { key: 'status', label: 'Trạng thái' },
      { key: 'amount', label: 'Tổng tiền', align: 'right' },
      { key: 'date', label: 'Ngày chứng từ' },
    ],
    formFields: [
      { key: 'code', label: 'Số phiếu mua', type: 'text', required: true },
      { key: 'supplier', label: 'Nhà cung cấp', type: 'text', required: true },
      { key: 'date', label: 'Ngày chứng từ', type: 'date', required: true },
      { key: 'amount', label: 'Tổng tiền dự kiến', type: 'number' },
      { key: 'note', label: 'Ghi chú', type: 'textarea' },
    ],
  },
  goodsReceipts: {
    key: 'goodsReceipts',
    title: 'Phiếu nhập',
    description: 'Luồng nhập kho, ghi sổ và cập nhật last price.',
    createLabel: 'Tạo phiếu nhập',
    updateLabel: 'Cập nhật phiếu nhập',
    columns: [
      { key: 'code', label: 'Số phiếu nhập' },
      { key: 'supplier', label: 'Nhà cung cấp' },
      { key: 'storeName', label: 'Kho' },
      { key: 'status', label: 'Trạng thái' },
      { key: 'date', label: 'Ngày nhập' },
    ],
    formFields: [
      { key: 'code', label: 'Số phiếu nhập', type: 'text', required: true },
      { key: 'supplier', label: 'Nhà cung cấp', type: 'text', required: true },
      { key: 'storeName', label: 'Kho', type: 'text', required: true },
      { key: 'date', label: 'Ngày nhập', type: 'date' },
      { key: 'note', label: 'Ghi chú', type: 'textarea' },
    ],
  },
  allocations: {
    key: 'allocations',
    title: 'Allocation',
    description: 'Phân bổ vật tư cho đơn hàng và quản lý khả dụng.',
    createLabel: 'Tạo allocation',
    updateLabel: 'Cập nhật allocation',
    columns: [
      { key: 'code', label: 'Mã allocation' },
      { key: 'orderNo', label: 'Đơn hàng' },
      { key: 'storeName', label: 'Kho' },
      { key: 'qty', label: 'SL đã gán', align: 'right' },
      { key: 'status', label: 'Trạng thái' },
    ],
    formFields: [
      { key: 'code', label: 'Mã allocation', type: 'text', required: true },
      { key: 'orderNo', label: 'Đơn hàng', type: 'text', required: true },
      { key: 'storeName', label: 'Kho', type: 'text', required: true },
      { key: 'qty', label: 'Số lượng gán', type: 'number', required: true },
      { key: 'note', label: 'Ghi chú', type: 'textarea' },
    ],
  },
  issues: {
    key: 'issues',
    title: 'Phiếu xuất',
    description: 'Xuất kho theo đơn hàng, giảm tồn thực tế và ghi nhận giá trị vật tư.',
    createLabel: 'Tạo phiếu xuất',
    updateLabel: 'Cập nhật phiếu xuất',
    columns: [
      { key: 'code', label: 'Số phiếu xuất' },
      { key: 'orderNo', label: 'Đơn hàng' },
      { key: 'storeName', label: 'Kho' },
      { key: 'status', label: 'Trạng thái' },
      { key: 'amount', label: 'Giá trị', align: 'right' },
    ],
    formFields: [
      { key: 'code', label: 'Số phiếu xuất', type: 'text', required: true },
      { key: 'orderNo', label: 'Đơn hàng', type: 'text', required: true },
      { key: 'storeName', label: 'Kho xuất', type: 'text', required: true },
      { key: 'amount', label: 'Giá trị tạm tính', type: 'number' },
      { key: 'note', label: 'Ghi chú', type: 'textarea' },
    ],
  },
  returns: {
    key: 'returns',
    title: 'Phiếu hoàn trả',
    description: 'Hoàn trả vật tư theo dòng xuất gốc.',
    createLabel: 'Tạo phiếu hoàn trả',
    updateLabel: 'Cập nhật phiếu hoàn trả',
    columns: [
      { key: 'code', label: 'Số phiếu hoàn trả' },
      { key: 'orderNo', label: 'Đơn hàng' },
      { key: 'storeName', label: 'Kho nhận lại' },
      { key: 'status', label: 'Trạng thái' },
      { key: 'qty', label: 'SL hoàn', align: 'right' },
    ],
    formFields: [
      { key: 'code', label: 'Số phiếu hoàn trả', type: 'text', required: true },
      { key: 'orderNo', label: 'Đơn hàng', type: 'text', required: true },
      { key: 'storeName', label: 'Kho nhận lại', type: 'text', required: true },
      { key: 'qty', label: 'Số lượng hoàn', type: 'number', required: true },
      { key: 'note', label: 'Lý do hoàn', type: 'textarea' },
    ],
  },
  stocktakes: {
    key: 'stocktakes',
    title: 'Phiếu kiểm kê',
    description: 'Snapshot, đếm kiểm kê và xác nhận chênh lệch.',
    createLabel: 'Tạo phiếu kiểm kê',
    updateLabel: 'Cập nhật phiếu kiểm kê',
    columns: [
      { key: 'code', label: 'Số phiếu kiểm kê' },
      { key: 'storeName', label: 'Kho' },
      { key: 'date', label: 'Ngày kiểm kê' },
      { key: 'status', label: 'Trạng thái' },
      { key: 'note', label: 'Mốc chốt' },
    ],
    formFields: [
      { key: 'code', label: 'Số phiếu kiểm kê', type: 'text', required: true },
      { key: 'storeName', label: 'Kho', type: 'text', required: true },
      { key: 'date', label: 'Ngày kiểm kê', type: 'date', required: true },
      { key: 'note', label: 'Mốc snapshot', type: 'textarea' },
    ],
  },
  monthlyReceiptsReport: {
    key: 'monthlyReceiptsReport',
    title: 'Báo cáo nhập theo tháng',
    description: 'Mock shell cho báo cáo nhập theo vật tư và nhà sản xuất.',
    createLabel: 'Tạo bộ lọc báo cáo',
    updateLabel: 'Cập nhật bộ lọc',
    columns: [
      { key: 'date', label: 'Tháng' },
      { key: 'name', label: 'Mặt hàng' },
      { key: 'manufacturer', label: 'Nhà sản xuất' },
      { key: 'qty', label: 'SL nhập', align: 'right' },
      { key: 'amount', label: 'Giá trị', align: 'right' },
    ],
    formFields: [
      { key: 'dateFrom', label: 'Từ tháng', type: 'date' },
      { key: 'dateTo', label: 'Đến tháng', type: 'date' },
      { key: 'manufacturer', label: 'Nhà sản xuất', type: 'text' },
      { key: 'note', label: 'Ghi chú', type: 'textarea' },
    ],
  },
  orderMaterialRatioReport: {
    key: 'orderMaterialRatioReport',
    title: 'Tỷ lệ vật tư / doanh số',
    description: 'Mock shell cho KPI vật tư đã xuất so với doanh số đơn hàng.',
    createLabel: 'Tạo bộ lọc KPI',
    updateLabel: 'Cập nhật bộ lọc KPI',
    columns: [
      { key: 'orderNo', label: 'Đơn hàng' },
      { key: 'name', label: 'Khách hàng / Công trình' },
      { key: 'amount', label: 'Giá trị vật tư', align: 'right' },
      { key: 'qty', label: 'Doanh số', align: 'right' },
      { key: 'status', label: 'Tỷ lệ / cảnh báo' },
    ],
    formFields: [
      { key: 'dateFrom', label: 'Từ ngày', type: 'date' },
      { key: 'dateTo', label: 'Đến ngày', type: 'date' },
      { key: 'status', label: 'Ngưỡng cảnh báo', type: 'select', options: INVENTORY_STATUSES.slice(0, 4) },
      { key: 'note', label: 'Ghi chú', type: 'textarea' },
    ],
  },
};

export const INVENTORY_CONTRACTS: Record<InventorySectionKey, InventoryContractDefinition[]> = {
  dashboard: [
    {
      name: 'Inventory dashboard summary',
      method: 'GET',
      path: '/api/inventory/dashboard?company_id={companyId}',
      purpose: 'Tổng hợp KPI đầu module và cảnh báo theo company hiện tại.',
      fields: ['company_id', 'receipt_count', 'issue_count', 'allocation_count', 'threshold_alert_count'],
    },
  ],
  catalogs: [
    {
      name: 'Item catalog list',
      method: 'GET',
      path: '/api/inventory/items?company_id={companyId}&search={search}',
      purpose: 'Lấy danh mục vật tư dùng chung; companyId chỉ xác định ngữ cảnh giá và giao dịch kho.',
      fields: ['id', 'company_id', 'code', 'name', 'manufacturer_id', 'base_uom_id', 'status'],
    },
    {
      name: 'Company supplier mapping',
      method: 'GET',
      path: '/api/inventory/company-suppliers?company_id={companyId}',
      purpose: 'Lấy danh sách supplier được phép dùng trong inventory của công ty.',
      fields: ['id', 'company_id', 'supplier_id', 'supplier_code_in_company', 'display_name', 'status'],
    },
  ],
  requests: [
    {
      name: 'Material requests',
      method: 'GET',
      path: '/api/inventory/material-requests?company_id={companyId}&status={status}',
      purpose: 'Danh sách đề xuất vật tư theo company.',
      fields: ['id', 'company_id', 'request_no', 'order_id', 'request_type', 'status', 'requested_at'],
    },
  ],
  purchasing: [
    {
      name: 'Purchase orders',
      method: 'GET',
      path: '/api/inventory/purchase-orders?company_id={companyId}&status={status}',
      purpose: 'Danh sách phiếu mua gắn supplier theo công ty.',
      fields: ['id', 'company_id', 'po_no', 'company_supplier_id', 'status', 'ordered_at', 'expected_date'],
    },
  ],
  receipts: [
    {
      name: 'Goods receipts',
      method: 'GET',
      path: '/api/inventory/goods-receipts?company_id={companyId}&store_id={storeId}',
      purpose: 'Danh sách phiếu nhập và trạng thái ghi sổ.',
      fields: ['id', 'company_id', 'receipt_no', 'purchase_order_id', 'store_id', 'status', 'posted_at'],
    },
  ],
  allocation: [
    {
      name: 'Allocations',
      method: 'GET',
      path: '/api/inventory/allocations?company_id={companyId}&order_id={orderId}',
      purpose: 'Danh sách allocation và khả dụng.',
      fields: ['id', 'company_id', 'allocation_no', 'order_id', 'store_id', 'qty_allocated', 'qty_issued', 'status'],
    },
  ],
  issues: [
    {
      name: 'Issues',
      method: 'GET',
      path: '/api/inventory/issues?company_id={companyId}&status={status}',
      purpose: 'Danh sách phiếu xuất kho theo công ty.',
      fields: ['id', 'company_id', 'issue_no', 'order_id', 'store_id', 'status', 'posted_at', 'total_amount'],
    },
  ],
  returns: [
    {
      name: 'Returns',
      method: 'GET',
      path: '/api/inventory/returns?company_id={companyId}&status={status}',
      purpose: 'Danh sách hoàn trả vật tư theo dòng xuất gốc.',
      fields: ['id', 'company_id', 'return_no', 'issue_line_id', 'store_id', 'status', 'posted_at'],
    },
  ],
  stocktakes: [
    {
      name: 'Stocktakes',
      method: 'GET',
      path: '/api/inventory/stocktakes?company_id={companyId}&store_id={storeId}',
      purpose: 'Danh sách phiếu kiểm kê và snapshot.',
      fields: ['id', 'company_id', 'stocktake_no', 'store_id', 'snapshot_at', 'status', 'confirmed_at'],
    },
  ],
  reports: [
    {
      name: 'Monthly receipt report',
      method: 'GET',
      path: '/api/inventory/reports/monthly-receipts?company_id={companyId}&date_from={dateFrom}&date_to={dateTo}',
      purpose: 'Báo cáo nhập theo tháng theo vật tư và nhà sản xuất.',
      fields: ['company_id', 'month', 'item_id', 'manufacturer_id', 'received_qty', 'received_amount'],
    },
    {
      name: 'Material ratio report',
      method: 'GET',
      path: '/api/inventory/reports/order-material-ratio?company_id={companyId}&threshold={threshold}',
      purpose: 'Báo cáo tỷ lệ tiền vật tư đã xuất trên doanh số đơn hàng.',
      fields: ['company_id', 'order_id', 'material_issued_amount', 'revenue_amount', 'ratio_percent', 'threshold_state'],
    },
  ],
};

export const getInventorySection = (sectionKey: InventorySectionKey) =>
  INVENTORY_SECTIONS.find((section) => section.key === sectionKey) || INVENTORY_SECTIONS[0];

export const getInventoryView = (viewKey: InventoryViewKey) => INVENTORY_VIEWS[viewKey];
